/**
 * Shopify-style product options/variants helpers.
 * Also supports legacy apparel shape: variants[{ size, colors: [{ color, stock, images }] }].
 */

function asTrimmedString(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value).trim();
    try {
        return String(value).trim();
    } catch {
        return '';
    }
}

/** Normalize API product into { options, variants } Shopify shape (read-only). */
export function normalizeProductVariants(product) {
    const rawVariants = Array.isArray(product?.variants) ? product.variants : [];
    const isLegacy = rawVariants.some((v) => Array.isArray(v?.colors) || (v?.size != null && v?.option1 == null && v?.stock == null));

    if (!isLegacy) {
        const options = Array.isArray(product?.options)
            ? product.options
                .map((o) => ({
                    name: asTrimmedString(o?.name),
                    values: [...new Set((o?.values || []).map(asTrimmedString).filter(Boolean))],
                }))
                .filter((o) => o.name)
                .slice(0, 3)
            : [];

        const variants = rawVariants.map((v, index) => ({
            ...v,
            _index: index,
            title:
                asTrimmedString(v?.title) ||
                [v?.option1, v?.option2, v?.option3].map(asTrimmedString).filter(Boolean).join(' / ') ||
                'Default Title',
            option1: asTrimmedString(v?.option1) || undefined,
            option2: asTrimmedString(v?.option2) || undefined,
            option3: asTrimmedString(v?.option3) || undefined,
            stock: Math.max(0, Number(v?.stock) || 0),
            images: Array.isArray(v?.images) ? v.images : [],
            available: v?.available !== false,
        }));

        return { options, variants };
    }

    const sizeValues = [];
    const colorValues = [];
    const flat = [];

    rawVariants.forEach((v, vi) => {
        const size = asTrimmedString(v?.size);
        if (size && !sizeValues.includes(size)) sizeValues.push(size);
        const colors = Array.isArray(v?.colors) ? v.colors : [];
        if (!colors.length) {
            if (size) {
                flat.push({
                    _index: flat.length,
                    title: size,
                    stock: 0,
                    option1: size,
                    images: [],
                    available: true,
                    _legacyVariantIndex: vi,
                });
            }
            return;
        }
        colors.forEach((c, ci) => {
            const color = asTrimmedString(c?.color);
            if (color && !colorValues.includes(color)) colorValues.push(color);
            const parts = [size, color].filter(Boolean);
            flat.push({
                _index: flat.length,
                title: parts.join(' / ') || 'Default Title',
                stock: Math.max(0, Number(c?.stock) || 0),
                option1: size || undefined,
                option2: color || undefined,
                images: Array.isArray(c?.images) ? c.images : [],
                available: true,
                _legacyVariantIndex: vi,
                _legacyColorIndex: ci,
            });
        });
    });

    const options = [];
    if (sizeValues.length && colorValues.length) {
        options.push({ name: 'Size', values: sizeValues });
        options.push({ name: 'Color', values: colorValues });
    } else if (sizeValues.length) {
        options.push({ name: 'Size', values: sizeValues });
    } else if (colorValues.length) {
        for (const f of flat) {
            if (f.option2 && !f.option1) {
                f.option1 = f.option2;
                delete f.option2;
                f.title = f.option1;
            }
        }
        options.push({ name: 'Color', values: colorValues });
    }

    return { options, variants: flat };
}

export function getOptionValueKey(optionIndex) {
    return `option${optionIndex + 1}`;
}

/** Build a selection map { [optionName]: value } from a flat variant. */
export function selectionFromVariant(options, variant) {
    const selection = {};
    (options || []).forEach((opt, i) => {
        const val = asTrimmedString(variant?.[getOptionValueKey(i)]);
        if (opt?.name && val) selection[opt.name] = val;
    });
    return selection;
}

/** Default selection = first in-stock variant, else first variant. */
export function getDefaultSelection(options, variants) {
    if (!variants?.length) return {};
    const preferred =
        variants.find((v) => v.available !== false && Number(v.stock) > 0) || variants[0];
    return selectionFromVariant(options, preferred);
}

export function findVariant(options, variants, selection = {}) {
    if (!variants?.length) return null;
    if (!options?.length) {
        return variants.find((v) => v.available !== false && Number(v.stock) > 0) || variants[0] || null;
    }

    return (
        variants.find((variant) =>
            options.every((opt, i) => {
                const selected = asTrimmedString(selection?.[opt.name]);
                const actual = asTrimmedString(variant?.[getOptionValueKey(i)]);
                if (!selected) return false;
                return selected.toLowerCase() === actual.toLowerCase();
            })
        ) || null
    );
}

/** Values still available for optionIndex given current partial selection. */
export function getAvailableOptionValues(options, variants, selection, optionIndex) {
    const opt = options?.[optionIndex];
    if (!opt) return [];

    const matching = (variants || []).filter((variant) => {
        if (variant.available === false) return false;
        return options.every((o, i) => {
            if (i === optionIndex) return true;
            const selected = asTrimmedString(selection?.[o.name]);
            if (!selected) return true;
            const actual = asTrimmedString(variant?.[getOptionValueKey(i)]);
            return selected.toLowerCase() === actual.toLowerCase();
        });
    });

    const values = [];
    matching.forEach((variant) => {
        const val = asTrimmedString(variant?.[getOptionValueKey(optionIndex)]);
        if (val && !values.some((v) => v.toLowerCase() === val.toLowerCase())) {
            values.push(val);
        }
    });
    return values.length ? values : [...(opt.values || [])];
}

export function selectionToSelectedOptions(options, selection = {}) {
    return (options || [])
        .map((opt) => ({
            name: asTrimmedString(opt?.name),
            value: asTrimmedString(selection?.[opt?.name]),
        }))
        .filter((o) => o.name && o.value);
}

export function formatSelectedOptionsLabel(selectedOptions, fallbackSize, fallbackColor) {
    if (Array.isArray(selectedOptions) && selectedOptions.length) {
        return selectedOptions
            .map((o) => asTrimmedString(o?.value) || asTrimmedString(o?.name))
            .filter(Boolean)
            .join(' / ');
    }
    return [fallbackSize, fallbackColor].map(asTrimmedString).filter(Boolean).join(' / ');
}

export function buildCartLineIdFromSelection(productId, selectedOptions = []) {
    const parts = (selectedOptions || [])
        .map((o) => `${asTrimmedString(o?.name)}=${asTrimmedString(o?.value)}`)
        .filter((p) => p !== '=');
    return `${productId}::${parts.join('|')}`;
}

/** Resolve unit price: variant.price override, else product effective price helper. */
export function getVariantUnitPrice(product, variant, getProductEffectivePrice) {
    if (variant?.price != null && variant.price !== '' && !Number.isNaN(Number(variant.price))) {
        const base = Number(variant.price);
        const discount = Number(product?.discount);
        if (Number.isFinite(discount) && discount > 0) {
            return base * (1 - Math.min(100, discount) / 100);
        }
        return base;
    }
    return typeof getProductEffectivePrice === 'function'
        ? getProductEffectivePrice(product)
        : Number(product?.price) || 0;
}

export function isColorOptionName(name) {
    const n = asTrimmedString(name).toLowerCase();
    return n === 'color' || n === 'colour' || n.includes('color') || n.includes('colour');
}
